import classNames from 'classnames'
import React from 'react'

import { IconButton } from '@ui/components/Button'
import { BellIcon } from '@ui/icons/Bell.icon'
import { CheckIcon } from '@ui/icons/Check.icon'
import { CloseIcon } from '@ui/icons/Close.icon'
import { WarningIcon } from '@ui/icons/Warning.icon'

import {
  detectHideGeneralNotificationsClientSide,
  detectHidePlatformNotificationsClientSide,
  detectHidePromoNotificationsClientSide,
} from '@model/Notification'

import styles from './notification.module.scss'

type NotificationVariantType = 'default' | 'warning' | 'danger' | 'success'

export interface NotificationProps {
  title?: string | React.ReactNode
  subtitle?: string | React.ReactNode
  dangerouslySetInnerHTML?: { __html: string }
  variant?: NotificationVariantType
  onClick?(): void
  onCancel?(): void
  wrapperClassName?: string
}

export const Notification: React.FC<NotificationProps> = (props) => {
  const { title, subtitle, variant = 'default', onClick, onCancel } = props
  const { dangerouslySetInnerHTML, wrapperClassName } = props

  return (
    <div
      className={classNames('notification', styles.notification, wrapperClassName, {
        ['is-' + variant]: true,
        'has-cursor-pointer': !!onClick,
      })}
      onClick={onClick}
    >
      <div className={styles.notificationContent}>
        {title && (
          <div className={styles.mainContent}>
            <IconFactory variant={variant} />
            <p className={styles.title}>{title}</p>
          </div>
        )}
        <div className={styles.mainContent}>
          {!title && <IconFactory variant={variant} />}
          {(subtitle || dangerouslySetInnerHTML) && (
            <p className={classNames(styles.subTitle, { [styles.extraMargin]: title })}>
              {isString(subtitle) ? <p>{subtitle}</p> : subtitle}
              {dangerouslySetInnerHTML && (
                <span
                  className={styles.innerHtml}
                  dangerouslySetInnerHTML={dangerouslySetInnerHTML}
                />
              )}
            </p>
          )}
        </div>
      </div>
      {onCancel && <IconButton.Ghost icon={CloseIcon} onClick={onCancel} />}
    </div>
  )
}

const isString = (value: string | React.ReactNode | undefined) => {
  return typeof value === 'string'
}

export const NotificationWarning: React.FC<Omit<NotificationProps, 'variant'>> = (props) => {
  return <Notification {...props} variant={'warning'} />
}

export const NotificationDanger: React.FC<Omit<NotificationProps, 'variant'>> = (props) => {
  return <Notification {...props} variant={'danger'} />
}

export const NotificationSuccess: React.FC<Omit<NotificationProps, 'variant'>> = (props) => {
  return <Notification {...props} variant={'success'} />
}

interface IconFactoryProps {
  variant: NotificationVariantType
}

const IconFactory: React.FC<IconFactoryProps> = (props) => {
  const { variant } = props

  if (variant === 'success') return <CheckIcon color='text-success-high-em' size={24} />
  if (variant === 'warning') return <WarningIcon color='text-warning-high-em' />
  if (variant === 'danger') return <WarningIcon color='text-danger-high-em' />

  return <BellIcon />
}

export interface NotificationsContextInterface {
  hidePlatformNotifications: boolean
  hideGeneralNotifications: boolean
  hidePromoNotifications: boolean
}

export function initiateNotificationsContext(): NotificationsContextInterface {
  return {
    hidePlatformNotifications: detectHidePlatformNotificationsClientSide(),
    hideGeneralNotifications: detectHideGeneralNotificationsClientSide(),
    hidePromoNotifications: detectHidePromoNotificationsClientSide(),
  }
}
